import { PLANETS, SIGNS } from '../constants.js';
import { calculateAspects } from '../aspects.js';
import {
  clearElement,
  createTitle,
  enrichPlanetList,
  formatDegreeInSign,
  polarToCartesian,
  svgElement,
  zodiacAngle,
} from '../utils.js';

const SIZE = 820;
const CENTER = SIZE / 2;

export function renderTransitWheel(container, snapshot, options = {}) {
  clearElement(container);

  const natal = normalizeChart(snapshot?.natal);
  const transit = normalizeChart(snapshot?.transit);
  const ascendant = Number(natal.ascendant ?? natal.planets.find((planet) => planet.id === 'ascendant')?.longitude ?? 0);
  const aspects = Array.isArray(snapshot?.aspects)
    ? snapshot.aspects
    : calculateAspects({
      source: transit.planets.filter((planet) => planet.id !== 'ascendant'),
      target: natal.planets.filter((planet) => planet.id !== 'ascendant'),
    });

  const svg = svgElement('svg', {
    viewBox: `0 0 ${SIZE} ${SIZE}`,
    className: 'aat-chart aat-transit-wheel',
    role: 'img',
    'aria-label': 'Inner birth chart and outer transit chart',
  });

  const background = svgElement('circle', {
    cx: CENTER,
    cy: CENTER,
    r: 394,
    className: 'wheel-background',
  });
  svg.append(background);

  const rings = [390, 352, 292, 222, 145];
  rings.forEach((radius, index) => {
    svg.append(svgElement('circle', {
      cx: CENTER,
      cy: CENTER,
      r: radius,
      className: `wheel-ring wheel-ring-${index + 1}`,
    }));
  });

  drawSignSectors(svg, ascendant);
  drawHouseCusps(svg, natal, ascendant);
  drawAspectLines(svg, aspects, ascendant, options);
  drawPlanets(svg, natal.planets, ascendant, {
    radius: 185,
    laneStep: 16,
    className: 'natal-planets',
    prefix: 'N',
  });
  drawPlanets(svg, transit.planets, ascendant, {
    radius: 318,
    laneStep: 16,
    className: 'transit-planets',
    prefix: 'T',
  });
  drawCenter(svg, snapshot);
  drawCardinalMarkers(svg);

  container.append(svg);
  return svg;
}

function normalizeChart(chart = {}) {
  const planets = enrichPlanetList(chart.planets || []);
  return {
    ...chart,
    planets,
    ascendant: chart.ascendant ?? planets.find((planet) => planet.id === 'ascendant')?.longitude,
    houses: Array.isArray(chart.houses) ? chart.houses.map(Number) : [],
  };
}

function drawSignSectors(svg, ascendant) {
  const group = svgElement('g', { className: 'wheel-sign-sectors' });

  for (let index = 0; index < 12; index += 1) {
    const boundaryLongitude = index * 30;
    const angle = zodiacAngle(boundaryLongitude, ascendant);
    const inner = polarToCartesian(CENTER, CENTER, 292, angle);
    const outer = polarToCartesian(CENTER, CENTER, 390, angle);
    group.append(svgElement('line', {
      x1: inner.x,
      y1: inner.y,
      x2: outer.x,
      y2: outer.y,
      className: 'sign-boundary',
    }));

    const labelAngle = zodiacAngle(boundaryLongitude + 15, ascendant);
    const labelPoint = polarToCartesian(CENTER, CENTER, 371, labelAngle);
    const sign = SIGNS[index];
    const text = svgElement('text', {
      x: labelPoint.x,
      y: labelPoint.y,
      className: 'sign-label',
      'text-anchor': 'middle',
      'dominant-baseline': 'central',
    });
    const glyph = svgElement('tspan', { className: 'sign-glyph', x: labelPoint.x, dy: '-0.35em' }, sign.glyph);
    const name = svgElement('tspan', { className: 'sign-name', x: labelPoint.x, dy: '1.2em' }, sign.short);
    text.append(glyph, name, createTitle(`${sign.name}: ${boundaryLongitude}°–${boundaryLongitude + 30}°`));
    group.append(text);
  }

  svg.append(group);
}

function drawHouseCusps(svg, chart, ascendant) {
  const group = svgElement('g', { className: 'wheel-house-cusps' });
  const cusps = chart.houses.length >= 12
    ? chart.houses.slice(0, 12)
    : Array.from({ length: 12 }, (_, index) => ascendant + index * 30);

  cusps.forEach((longitude, index) => {
    const angle = zodiacAngle(longitude, ascendant);
    const from = polarToCartesian(CENTER, CENTER, 145, angle);
    const to = polarToCartesian(CENTER, CENTER, 292, angle);
    group.append(svgElement('line', {
      x1: from.x,
      y1: from.y,
      x2: to.x,
      y2: to.y,
      className: index % 3 === 0 ? 'house-cusp house-cusp-angular' : 'house-cusp',
    }));

    const nextCusp = cusps[(index + 1) % 12] + (index === 11 ? 360 : 0);
    const midpoint = longitude + (((nextCusp - longitude + 360) % 360) / 2);
    const labelPoint = polarToCartesian(CENTER, CENTER, 270, zodiacAngle(midpoint, ascendant));
    group.append(svgElement('text', {
      x: labelPoint.x,
      y: labelPoint.y,
      className: 'house-number',
      'text-anchor': 'middle',
      'dominant-baseline': 'central',
    }, String(index + 1)));
  });

  svg.append(group);
}

function drawAspectLines(svg, aspects, ascendant, options) {
  const group = svgElement('g', { className: 'wheel-aspects' });
  const maximum = Number(options.maxAspectLines ?? 60);

  aspects.slice(0, maximum).forEach((aspect) => {
    const fromPoint = polarToCartesian(CENTER, CENTER, 310, zodiacAngle(aspect.from.longitude, ascendant));
    const toPoint = polarToCartesian(CENTER, CENTER, 208, zodiacAngle(aspect.to.longitude, ascendant));
    const line = svgElement('line', {
      x1: fromPoint.x,
      y1: fromPoint.y,
      x2: toPoint.x,
      y2: toPoint.y,
      className: `aspect-line aspect-${aspect.type}`,
      'data-orb': aspect.orb?.toFixed?.(2) ?? '',
    });
    line.append(createTitle(`${aspect.from.name} ${aspect.label} ${aspect.to.name}; orb ${Number(aspect.orb).toFixed(2)}°`));
    group.append(line);
  });

  svg.append(group);
}

function drawPlanets(svg, planets, ascendant, layoutOptions) {
  const filtered = planets.filter((planet) => Number.isFinite(planet.longitude));
  const layout = resolveCollisions(filtered, ascendant, layoutOptions.radius, layoutOptions.laneStep);
  const group = svgElement('g', { className: `wheel-planets ${layoutOptions.className}` });

  layout.forEach(({ planet, radius, angle }) => {
    const point = polarToCartesian(CENTER, CENTER, radius, angle);
    const marker = svgElement('g', {
      className: `planet-marker ${planet.className || PLANETS[planet.id]?.className || 'planet-generic'}`,
      transform: `translate(${point.x} ${point.y})`,
      tabindex: '0',
      role: 'img',
      'aria-label': `${layoutOptions.prefix === 'T' ? 'Transit' : 'Natal'} ${planet.name} ${formatDegreeInSign(planet.degreeInSign)} ${planet.sign}${planet.retrograde ? ' retrograde' : ''}`,
    });

    marker.append(svgElement('circle', { cx: 0, cy: 0, r: 14, className: 'planet-marker-bg' }));
    marker.append(svgElement('text', {
      x: 0,
      y: -1,
      className: 'planet-marker-label',
      'text-anchor': 'middle',
      'dominant-baseline': 'central',
    }, `${planet.short}${planet.retrograde ? 'R' : ''}`));
    marker.append(svgElement('text', {
      x: 0,
      y: 20,
      className: 'planet-marker-degree',
      'text-anchor': 'middle',
    }, formatDegreeInSign(planet.degreeInSign)));
    marker.append(createTitle([
      `${layoutOptions.prefix === 'T' ? 'Transit' : 'Natal'} ${planet.name}`,
      `${formatDegreeInSign(planet.degreeInSign)} ${planet.sign}`,
      `${planet.nakshatra}, pada ${planet.pada}`,
      planet.retrograde ? 'Retrograde' : 'Direct',
    ].join(' — ')));
    group.append(marker);
  });

  svg.append(group);
}

function resolveCollisions(planets, ascendant, baseRadius, laneStep) {
  const sorted = planets
    .map((planet) => ({ planet, angle: zodiacAngle(planet.longitude, ascendant) }))
    .sort((a, b) => a.angle - b.angle);

  const lanes = [];
  return sorted.map((item) => {
    let lane = 0;
    while (lane < lanes.length && angularDifference(lanes[lane], item.angle) < 8) lane += 1;
    lanes[lane] = item.angle;
    const inward = lane % 2 === 0 ? 1 : -1;
    const offsetIndex = Math.ceil(lane / 2);
    return {
      ...item,
      radius: baseRadius + inward * offsetIndex * laneStep,
    };
  });
}

function angularDifference(a, b) {
  const delta = Math.abs(a - b);
  return Math.min(delta, 360 - delta);
}

function drawCenter(svg, snapshot) {
  const group = svgElement('g', { className: 'wheel-center' });
  group.append(svgElement('circle', {
    cx: CENTER,
    cy: CENTER,
    r: 138,
    className: 'wheel-center-disc',
  }));

  const title = snapshot?.meta?.title || 'Professional Tools';
  const dateLabel = snapshot?.meta?.displayDateTime || snapshot?.meta?.localDateTime || snapshot?.meta?.atUtc || '';
  const subtitle = snapshot?.meta?.subtitle || 'Inner: Birth Chart · Outer: Gochar';
  const lines = [title, dateLabel, subtitle].filter(Boolean);
  const startY = CENTER - ((lines.length - 1) * 18) / 2;

  lines.forEach((line, index) => {
    group.append(svgElement('text', {
      x: CENTER,
      y: startY + index * 22,
      className: index === 0 ? 'wheel-center-title' : 'wheel-center-text',
      'text-anchor': 'middle',
      'dominant-baseline': 'central',
    }, line));
  });

  svg.append(group);
}

function drawCardinalMarkers(svg) {
  const group = svgElement('g', { className: 'wheel-cardinal-markers' });
  const markers = [
    { label: 'ASC', angle: 270 },
    { label: 'MC', angle: 0 },
    { label: 'DSC', angle: 90 },
    { label: 'IC', angle: 180 },
  ];
  markers.forEach(({ label, angle }) => {
    const point = polarToCartesian(CENTER, CENTER, 402, angle);
    group.append(svgElement('text', {
      x: point.x,
      y: point.y,
      className: 'cardinal-label',
      'text-anchor': 'middle',
      'dominant-baseline': 'central',
    }, label));
  });
  svg.append(group);
}
